import React, { useEffect, useRef } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Handshake } from "lucide-react";
import MarkThumbnail from "./MarkThumbnail";
import GoodsAndServices from "./GoodsAndServices";
import { StatusBadge } from "../ui/Badge";
import {
  EM_DASH,
  SIMILARITY_TEXT_CLASS,
  formatDate,
  formatOwners,
  officeLabel,
  similarityTone,
} from "../../lib/format";

/**
 * The register table.
 *
 * Column set mirrors what a TMview result row carries, plus the AI
 * similarity score. Every column except the mark itself can be hidden, and
 * every column is sortable — clicking a header cycles ascending, descending,
 * then back to the register's own order.
 */

export const COLUMNS = [
  { key: "applicant", label: "Owner", sortKey: "applicant" },
  {
    key: "applicationNumber",
    label: "Application no.",
    sortKey: "applicationNumber",
  },
  { key: "filingDate", label: "Application date", sortKey: "filingDate" },
  { key: "niceClasses", label: "Nice classes", sortKey: "niceClasses" },
  { key: "goodsAndServices", label: "Goods & services", sortKey: null },
  { key: "jurisdiction", label: "Jurisdiction", sortKey: "jurisdiction" },
  { key: "status", label: "Status", sortKey: "status" },
  {
    key: "similarity",
    label: "AI similarity",
    sortKey: "similarity",
    align: "right",
  },
];

// Everything is on by default — goods & services is the column people open
// this table for. Add a key here to start with it hidden (it can still be
// switched on from the Columns menu).
export const DEFAULT_HIDDEN_COLUMNS = new Set();

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  className = "",
}) {
  const active = sort.key === sortKey;
  const Icon = !active
    ? ChevronsUpDown
    : sort.direction === "asc"
      ? ArrowUp
      : ArrowDown;

  return (
    <th
      scope="col"
      className={`px-3 py-2.5 text-micro font-semibold text-ink-600 ${
        align === "right" ? "text-right" : "text-left"
      } ${className}`}
      aria-sort={
        active
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 transition-colors hover:text-ink-900 ${
          active ? "text-ink-900" : ""
        } ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        <Icon
          className={`h-3 w-3 ${active ? "text-[#8f5eeb]-600" : "text-ink-300"}`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

export default function ResultsTable({
  rows,
  startIndex = 0,
  visibleColumns,
  sort,
  onSort,
  selected,
  onToggleRow,
  onToggleAll,
  similarityMap,
  onOpenImage,
  onOpenDetail,
  onFindLicensing,
  isDetailLoading = () => false,
}) {
  const headerCheckbox = useRef(null);

  const pageIds = rows.map((r) => r.trademarkId);
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someSelected = pageIds.some((id) => selected.has(id));

  useEffect(() => {
    if (headerCheckbox.current) {
      headerCheckbox.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const show = (key) => visibleColumns[key];
  const checkboxClass =
    "h-4 w-4 rounded border-ink-300 text-ink-900 focus:ring-seal-500/40 cursor-pointer";

  return (
    <div className="overflow-x-auto border-x border-ink-200 bg-white">
      <table className="w-full min-w-[1080px] border-collapse text-sm">
        <thead className="border-y border-ink-200 bg-paper-sunken/60">
          <tr>
            <th scope="col" className="w-10 px-3 py-2.5">
              <input
                ref={headerCheckbox}
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                aria-label="Select every row on this page"
                className={checkboxClass}
              />
            </th>
            <th
              scope="col"
              className="w-12 px-3 py-2.5 text-micro font-semibold text-ink-600"
            >
              Result
            </th>
            <th
              scope="col"
              className="w-16 px-3 py-2.5 text-micro font-semibold text-ink-600"
            >
              Image
            </th>
            <SortHeader
              label="Wordmark"
              sortKey="wordMark"
              sort={sort}
              onSort={onSort}
              className="min-w-[180px]"
            />
            {COLUMNS.filter((c) => show(c.key)).map((column) =>
              column.sortKey ? (
                <SortHeader
                  key={column.key}
                  label={column.label}
                  sortKey={column.sortKey}
                  sort={sort}
                  onSort={onSort}
                  align={column.align}
                />
              ) : (
                <th
                  key={column.key}
                  scope="col"
                  className="px-3 py-2.5 text-left text-micro font-semibold text-ink-600"
                >
                  {column.label}
                </th>
              ),
            )}
            {onFindLicensing && (
              <th
                scope="col"
                className="w-10 px-3 py-2.5 text-micro font-semibold text-ink-600"
              >
                <span className="sr-only">Potential licensing</span>
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const isSelected = selected.has(row.trademarkId);
            const score = similarityMap.get(row.trademarkId);

            return (
              <tr
                key={row.trademarkId || index}
                onClick={() => onOpenDetail?.(row)}
                tabIndex={onOpenDetail ? 0 : undefined}
                role={onOpenDetail ? "button" : undefined}
                onKeyDown={(e) => {
                  if (
                    onOpenDetail &&
                    e.target === e.currentTarget &&
                    (e.key === "Enter" || e.key === " ")
                  ) {
                    e.preventDefault();
                    onOpenDetail(row);
                  }
                }}
                className={`border-b border-ink-100 align-top transition-colors last:border-b-0 hover:bg-[#8f5eeb]-100/40 ${
                  onOpenDetail ? "cursor-pointer" : ""
                } ${isSelected ? "bg-[#8f5eeb]-100/60" : ""}`}
              >
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleRow(row.trademarkId)}
                    aria-label={`Select ${row.wordMark || "this record"}`}
                    className={checkboxClass}
                  />
                </td>

                <td className="whitespace-nowrap px-3 py-2 text-xs text-ink-400">
                  {startIndex + index + 1}
                </td>

                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <MarkThumbnail
                    wordMark={row.wordMark}
                    imageUrl={row.imageUrl}
                    onClick={() =>
                      onOpenImage({ url: row.imageUrl, wordMark: row.wordMark })
                    }
                  />
                </td>

                <td className="px-3 py-2">
                  <span className="font-semibold text-ink-900">
                    {row.wordMark || EM_DASH}
                  </span>
                  {row.markFeature && (
                    <span className="mt-0.5 block text-micro text-ink-500">
                      {row.markFeature}
                    </span>
                  )}
                </td>

                {show("applicant") && (
                  <td className="max-w-[260px] px-3 py-2 align-top text-ink-700">
                    {formatOwners(row) ? (
                      <span
                        className="line-clamp-2 break-words"
                        title={formatOwners(row)}
                      >
                        {formatOwners(row)}
                      </span>
                    ) : (
                      <span className="text-ink-300">
                        {isDetailLoading(row) ? "…" : EM_DASH}
                      </span>
                    )}
                  </td>
                )}

                {show("applicationNumber") && (
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-ink-700">
                    {row.applicationNumber || EM_DASH}
                  </td>
                )}

                {show("filingDate") && (
                  <td className="whitespace-nowrap px-3 py-2 text-ink-700">
                    {formatDate(row.filingDate)}
                  </td>
                )}

                {show("niceClasses") && (
                  <td className="px-3 py-2 text-ink-700">
                    {row.niceClasses?.length
                      ? row.niceClasses.join(", ")
                      : EM_DASH}
                  </td>
                )}

                {show("goodsAndServices") && (
                  <td className="min-w-[260px] max-w-[380px] px-3 py-2 align-top text-ink-700">
                    <GoodsAndServices
                      items={row.goodsAndServices}
                      loading={isDetailLoading(row)}
                      lines={2}
                      emptyLabel={EM_DASH}
                    />
                  </td>
                )}

                {show("jurisdiction") && (
                  <td className="whitespace-nowrap px-3 py-2 text-ink-700">
                    {officeLabel(row.jurisdiction)}
                  </td>
                )}

                {show("status") && (
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                )}

                {show("similarity") && (
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {score === undefined ? (
                      <span className="text-ink-300">{EM_DASH}</span>
                    ) : (
                      <span
                        className={`text-xs font-bold ${SIMILARITY_TEXT_CLASS[similarityTone(score)]}`}
                      >
                        {score}%
                      </span>
                    )}
                  </td>
                )}

                {onFindLicensing && (
                  <td
                    className="px-3 py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => onFindLicensing(row)}
                      title="Find potential licensing"
                      aria-label={`Find potential licensing for ${row.wordMark || "this mark"}`}
                      className="rounded p-1.5 text-ink-400 hover:bg-[#8f5eeb]-100 hover:text-[#8f5eeb]-600"
                    >
                      <Handshake className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
